import React, { useRef, useState, useEffect, forwardRef } from "react";
import { toPng } from "html-to-image";
import { jsPDF } from "jspdf";
import { toast } from "sonner";
import { Sparkles, Download, FileDown, Save, Loader2, Copy, Trash2, FolderOpen, Wand2, Image as ImageIcon, Palette, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ImageUpload } from "@/components/ImageUpload";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import symbolWhite from "@/assets/symbol_white.png";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MATERIAL_ROLES = ["super_admin", "national_admin", "diocesan_admin", "community_coordinator"];

const TYPES = {
  flyer: { w: 794, h: 1123, orient: "portrait" },
  poster: { w: 794, h: 1123, orient: "portrait" },
  social_square: { w: 1080, h: 1080, orient: "square" },
  social_story: { w: 1080, h: 1920, orient: "portrait" },
  invite: { w: 794, h: 1123, orient: "portrait" },
};

const TEMPLATES = [
  { id: "classic", label: "Clássico" },
  { id: "elegant", label: "Elegante" },
  { id: "banner", label: "Faixa dourada" },
];

const DEFAULT_FIELDS = { title: "", subtitle: "", date: "", location: "", body: "", footer: "tercodoshomens.eu" };

const LANG_NAMES = { pt: "PT", es: "ES", en: "EN", fr: "FR", it: "IT", pl: "PL", de: "DE", uk: "UK", ru: "RU" };
const DATE_LABELS = { pt: "Data", es: "Fecha", en: "Date", fr: "Date", it: "Data", pl: "Data", de: "Datum", uk: "Дата", ru: "Дата" };
const LOC_LABELS = { pt: "Local", es: "Lugar", en: "Location", fr: "Lieu", it: "Luogo", pl: "Miejsce", de: "Ort", uk: "Місце", ru: "Место" };

/* ---------------------------------------------------------------- Canvas */
const MaterialCanvas = forwardRef(function MaterialCanvas({ type, template, fields, bgUrl, bgColor, bgMode, dateLabel = "Data", locLabel = "Local" }, ref) {
  const dim = TYPES[type] || TYPES.flyer;
  const hasImage = bgMode !== "color" && !!bgUrl;
  const baseFont = dim.w / 794; // scale text with size

  const shell = {
    width: dim.w, height: dim.h, position: "relative", overflow: "hidden",
    backgroundColor: bgMode === "color" ? bgColor : "#0b1f47",
    color: "#fff", fontFamily: "'Cormorant Garamond', Georgia, serif",
  };
  const imgStyle = {
    position: "absolute", inset: 0, width: "100%", height: "100%",
    objectFit: "cover", zIndex: 0,
  };

  const em = (px) => `${px * baseFont}px`;

  // overlay + layout per template
  let overlay, content;
  if (template === "elegant") {
    overlay = { position: "absolute", left: 0, right: 0, bottom: 0, height: "52%", zIndex: 1,
      background: "linear-gradient(to top, rgba(9,24,58,0.98) 55%, rgba(9,24,58,0))" };
    content = { position: "absolute", zIndex: 2, left: em(64), right: em(64), bottom: em(70), textAlign: "left" };
  } else if (template === "banner") {
    overlay = { position: "absolute", inset: em(28), zIndex: 1, border: `${em(4)} solid #caa24a`, borderRadius: em(10) };
    content = { position: "absolute", zIndex: 2, inset: em(70), display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center", textAlign: "center" };
  } else {
    overlay = { position: "absolute", inset: 0, zIndex: 1,
      background: "linear-gradient(to top, rgba(7,17,42,0.92) 8%, rgba(7,17,42,0.25) 45%, rgba(7,17,42,0.55) 100%)" };
    content = { position: "absolute", zIndex: 2, inset: 0, padding: em(64), display: "flex",
      flexDirection: "column", alignItems: "center", justifyContent: "space-between", textAlign: "center" };
  }

  const Chip = ({ label, value }) => value ? (
    <div style={{ display: "inline-flex", flexDirection: "column", alignItems: template === "elegant" ? "flex-start" : "center",
      background: "rgba(202,162,74,0.16)", border: "1px solid rgba(202,162,74,0.5)", borderRadius: em(10),
      padding: `${em(10)} ${em(20)}`, margin: em(6) }}>
      <span style={{ fontSize: em(15), letterSpacing: em(2), textTransform: "uppercase", color: "#e6c877", fontFamily: "Georgia, serif" }}>{label}</span>
      <span style={{ fontSize: em(28), fontWeight: 600 }}>{value}</span>
    </div>
  ) : null;

  const Title = () => (
    <>
      {fields.subtitle ? <div style={{ fontSize: em(24), letterSpacing: em(4), textTransform: "uppercase",
        color: "#e6c877", fontFamily: "Georgia, serif", marginBottom: em(10) }}>{fields.subtitle}</div> : null}
      <div style={{ fontSize: em(66), fontWeight: 700, lineHeight: 1.05, textShadow: "0 2px 18px rgba(0,0,0,0.45)" }}>
        {fields.title || "Terço dos Homens"}
      </div>
      {fields.body ? <div style={{ fontSize: em(27), lineHeight: 1.4, marginTop: em(22), opacity: 0.95,
        fontFamily: "Georgia, serif", maxWidth: em(620), whiteSpace: "pre-wrap" }}>{fields.body}</div> : null}
    </>
  );

  const Logo = () => (
    <img src={symbolWhite} crossOrigin="anonymous" alt="" style={{ width: em(84), height: em(84), objectFit: "contain" }} />
  );

  const Footer = () => (
    <div style={{ fontSize: em(22), letterSpacing: em(1), color: "#e6c877", fontFamily: "Georgia, serif" }}>{fields.footer}</div>
  );

  return (
    <div ref={ref} style={shell}>
      {hasImage && <img src={bgUrl} crossOrigin="anonymous" alt="" style={imgStyle} />}
      <div style={overlay} />
      {template === "elegant" ? (
        <>
          <div style={{ position: "absolute", top: em(48), left: em(64), zIndex: 2 }}><Logo /></div>
          <div style={content}>
            <div style={{ width: em(70), height: em(4), background: "#caa24a", marginBottom: em(24) }} />
            <Title />
            <div style={{ display: "flex", flexWrap: "wrap", marginTop: em(24), marginLeft: em(-6) }}>
              <Chip label={dateLabel} value={fields.date} />
              <Chip label={locLabel} value={fields.location} />
            </div>
            <div style={{ marginTop: em(26) }}><Footer /></div>
          </div>
        </>
      ) : (
        <div style={content}>
          <Logo />
          <div>
            <Title />
          </div>
          <div>
            <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", marginBottom: em(18) }}>
              <Chip label={dateLabel} value={fields.date} />
              <Chip label={locLabel} value={fields.location} />
            </div>
            <Footer />
          </div>
        </div>
      )}
    </div>
  );
});

/* ---------------------------------------------------------------- Page */
export default function Materials() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const canvasRef = useRef(null);
  const [tab, setTab] = useState("create");
  const [type, setType] = useState("flyer");
  const [template, setTemplate] = useState("classic");
  const [fields, setFields] = useState(DEFAULT_FIELDS);
  const [bgMode, setBgMode] = useState("ai");
  const [bgUrl, setBgUrl] = useState("");
  const [bgColor, setBgColor] = useState("#0b1f47");
  const [aiPrompt, setAiPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [library, setLibrary] = useState([]);
  const [translations, setTranslations] = useState({});
  const [activeLang, setActiveLang] = useState("pt");
  const [translating, setTranslating] = useState(false);

  const canUse = user && MATERIAL_ROLES.includes(user.role);

  const loadLibrary = async () => {
    try {
      const { data } = await api.get("/materials");
      setLibrary(data);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { if (canUse) loadLibrary(); }, [canUse]);

  const setField = (k, v) => setFields((f) => ({ ...f, [k]: v }));

  const tr = activeLang !== "pt" ? translations[activeLang] : null;
  const displayFields = tr
    ? { ...fields, title: tr.title || fields.title, subtitle: tr.subtitle || fields.subtitle, body: tr.body || fields.body }
    : fields;

  const translateAI = async () => {
    if (!fields.title && !fields.subtitle && !fields.body) { toast.error(t("mat_field_title")); return; }
    setTranslating(true);
    try {
      const { data } = await api.post("/materials/translate", { title: fields.title, subtitle: fields.subtitle, body: fields.body });
      setTranslations(data.translations || {});
      toast.success(t("mat_translated"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTranslating(false); }
  };

  const generateArt = async () => {
    if (!aiPrompt.trim()) { toast.error(t("mat_ai_prompt")); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/materials/generate-art", { prompt: aiPrompt, orientation: TYPES[type].orient });
      setBgUrl(`${BACKEND_URL}${data.url}`);
      setBgMode("ai");
      toast.success(t("mat_generated"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setGenerating(false); }
  };

  const renderPng = async (pixelRatio) => {
    const dim = TYPES[type];
    return toPng(canvasRef.current, {
      width: dim.w, height: dim.h, pixelRatio, cacheBust: true, skipFonts: true, style: { transform: "none" },
    });
  };

  const exportPng = async () => {
    try {
      const dataUrl = await renderPng(2);
      const a = document.createElement("a");
      a.href = dataUrl; a.download = `${(displayFields.title || t("mat_type_" + type)).slice(0, 40)}-${activeLang}.png`; a.click();
    } catch (e) { toast.error("Erro ao exportar"); }
  };

  const exportPdf = async () => {
    try {
      const dim = TYPES[type];
      const dataUrl = await renderPng(2);
      const pdf = new jsPDF({ orientation: dim.w > dim.h ? "landscape" : "portrait", unit: "px", format: [dim.w, dim.h] });
      pdf.addImage(dataUrl, "PNG", 0, 0, dim.w, dim.h);
      pdf.save(`${(displayFields.title || t("mat_type_" + type)).slice(0, 40)}-${activeLang}.pdf`);
    } catch (e) { toast.error("Erro ao exportar"); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const dataUrl = await renderPng(1);
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "thumb.png", { type: "image/png" });
      const fd = new FormData(); fd.append("file", file);
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const payload = {
        title: fields.title || t("mat_type_" + type), type, template_id: template, lang: activeLang, fields, translations,
        background_url: bgMode === "color" ? null : (bgUrl || null),
        background_color: bgMode === "color" ? bgColor : null,
        thumbnail_url: `${BACKEND_URL}${up.data.url}`,
      };
      if (editingId) await api.put(`/materials/${editingId}`, payload);
      else await api.post("/materials", payload);
      toast.success(t("mat_saved"));
      loadLibrary();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const openMaterial = (m, duplicate = false) => {
    setType(m.type || "flyer");
    setTemplate(m.template_id || "classic");
    setFields({ ...DEFAULT_FIELDS, ...(m.fields || {}) });
    setTranslations(m.translations || {});
    setActiveLang("pt");
    if (m.background_color) { setBgMode("color"); setBgColor(m.background_color); setBgUrl(""); }
    else { setBgMode(m.background_url ? "upload" : "ai"); setBgUrl(m.background_url || ""); }
    setEditingId(duplicate ? null : m.id);
    setTab("create");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const removeMaterial = async (id) => {
    if (!window.confirm(t("mat_confirm_delete"))) return;
    try { await api.delete(`/materials/${id}`); loadLibrary(); toast.success(t("mat_delete")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const newMaterial = () => { setEditingId(null); setFields(DEFAULT_FIELDS); setBgUrl(""); setBgMode("ai"); setAiPrompt(""); setTranslations({}); setActiveLang("pt"); };

  if (user === null) return <div className="min-h-[60vh] flex items-center justify-center text-th-blue">{t("loading")}</div>;
  if (!canUse) return (
    <div className="min-h-[60vh] flex items-center justify-center px-6" data-testid="materials-no-access">
      <div className="max-w-md text-center bg-white rounded-2xl border border-slate-200 p-10 shadow-sm">
        <Wand2 className="w-10 h-10 text-th-gold mx-auto mb-4" />
        <h1 className="font-display text-2xl text-th-blue font-bold">{t("mat_title")}</h1>
        <p className="mt-3 text-slate-600">{t("mat_no_access")}</p>
      </div>
    </div>
  );

  // preview scaling
  const dim = TYPES[type];
  const previewW = 340;
  const scale = previewW / dim.w;

  const TypeBtn = ({ id }) => (
    <button data-testid={`mat-type-${id}`} onClick={() => setType(id)}
      className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${type === id ? "bg-th-blue text-white border-th-blue" : "bg-white text-th-blue border-slate-200 hover:border-th-blue/50"}`}>
      {t("mat_type_" + id)}
    </button>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="materials-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-th-gold text-xs font-semibold uppercase tracking-widest mb-2">{t("hero_eyebrow")}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-th-blue">{t("mat_title")}</h1>
          <p className="mt-2 text-slate-600 max-w-2xl">{t("mat_subtitle")}</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="mat-tab-create" onClick={() => setTab("create")}
            className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === "create" ? "bg-th-blue text-white" : "bg-white text-th-blue border border-slate-200"}`}>{t("mat_tab_create")}</button>
          <button data-testid="mat-tab-library" onClick={() => { setTab("library"); loadLibrary(); }}
            className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === "library" ? "bg-th-blue text-white" : "bg-white text-th-blue border border-slate-200"}`}>{t("mat_tab_library")} · {library.length}</button>
        </div>
      </div>

      {tab === "create" ? (
        <div className="grid lg:grid-cols-[1fr_380px] gap-8">
          {/* Editor */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <Label className="text-th-blue font-semibold">{t("mat_type")}</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {Object.keys(TYPES).map((id) => <TypeBtn key={id} id={id} />)}
              </div>
              <Label className="text-th-blue font-semibold block mt-5">{t("mat_template")}</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {TEMPLATES.map((tp) => (
                  <button key={tp.id} data-testid={`mat-template-${tp.id}`} onClick={() => setTemplate(tp.id)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${template === tp.id ? "bg-th-gold text-th-bluedark border-th-gold" : "bg-white text-th-blue border-slate-200 hover:border-th-gold/60"}`}>
                    {tp.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-5 grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label>{t("mat_field_title")}</Label>
                <Input data-testid="mat-field-title" value={fields.title} onChange={(e) => setField("title", e.target.value)} className="mt-1" />
              </div>
              <div className="sm:col-span-2">
                <Label>{t("mat_field_subtitle")}</Label>
                <Input data-testid="mat-field-subtitle" value={fields.subtitle} onChange={(e) => setField("subtitle", e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>{t("mat_field_date")}</Label>
                <Input data-testid="mat-field-date" value={fields.date} onChange={(e) => setField("date", e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>{t("mat_field_location")}</Label>
                <Input data-testid="mat-field-location" value={fields.location} onChange={(e) => setField("location", e.target.value)} className="mt-1" />
              </div>
              <div className="sm:col-span-2">
                <Label>{t("mat_field_body")}</Label>
                <Textarea data-testid="mat-field-body" value={fields.body} onChange={(e) => setField("body", e.target.value)} rows={2} className="mt-1" />
              </div>
              <div className="sm:col-span-2">
                <Label>{t("mat_field_footer")}</Label>
                <Input data-testid="mat-field-footer" value={fields.footer} onChange={(e) => setField("footer", e.target.value)} className="mt-1" />
              </div>
              <div className="sm:col-span-2 border-t border-slate-100 pt-4">
                <Button type="button" data-testid="mat-translate-ai" variant="outline" onClick={translateAI} disabled={translating}
                  className="border-th-gold text-th-bluedark hover:bg-th-gold/10">
                  {translating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
                  {translating ? t("mat_translating") : t("mat_translate_ai")}
                </Button>
                {Object.keys(translations).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3" data-testid="mat-lang-chips">
                    {["pt", ...Object.keys(translations)].map((lc) => (
                      <button key={lc} data-testid={`mat-lang-${lc}`} onClick={() => setActiveLang(lc)}
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${activeLang === lc ? "bg-th-blue text-white border-th-blue" : "bg-white text-th-blue border-slate-200 hover:border-th-blue/40"}`}>
                        {LANG_NAMES[lc] || lc.toUpperCase()}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Background */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <Label className="text-th-blue font-semibold">{t("mat_background")}</Label>
              <div className="flex flex-wrap gap-2 mt-2 mb-4">
                {[{ id: "ai", icon: Sparkles, label: t("mat_bg_ai") }, { id: "upload", icon: Upload, label: t("mat_bg_upload") }, { id: "color", icon: Palette, label: t("mat_bg_color") }].map((o) => (
                  <button key={o.id} data-testid={`mat-bg-${o.id}`} onClick={() => setBgMode(o.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${bgMode === o.id ? "bg-th-blue text-white border-th-blue" : "bg-white text-th-blue border-slate-200 hover:border-th-blue/50"}`}>
                    <o.icon className="w-4 h-4" />{o.label}
                  </button>
                ))}
              </div>
              {bgMode === "ai" && (
                <div className="space-y-2">
                  <Textarea data-testid="mat-ai-prompt" value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)}
                    placeholder={t("mat_ai_hint")} rows={2} />
                  <Button data-testid="mat-ai-generate" onClick={generateArt} disabled={generating}
                    className="bg-th-gold text-th-bluedark hover:bg-th-goldlight font-semibold">
                    {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
                    {generating ? t("mat_ai_generating") : t("mat_ai_generate")}
                  </Button>
                </div>
              )}
              {bgMode === "upload" && (
                <ImageUpload value={bgUrl} onChange={setBgUrl} testid="mat-bg-upload-img" />
              )}
              {bgMode === "color" && (
                <div className="flex items-center gap-3">
                  <input type="color" data-testid="mat-bg-color-input" value={bgColor} onChange={(e) => setBgColor(e.target.value)}
                    className="w-14 h-10 rounded border border-slate-200 cursor-pointer" />
                  <div className="flex gap-2">
                    {["#0b1f47", "#7a1f2b", "#1d4d3e", "#3b2a5a", "#8a6d1f"].map((c) => (
                      <button key={c} onClick={() => setBgColor(c)} style={{ background: c }}
                        className="w-8 h-8 rounded-full border-2 border-white shadow" />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Preview + actions */}
          <div className="lg:sticky lg:top-24 self-start">
            <div className="bg-slate-100 rounded-2xl border border-slate-200 p-5 flex flex-col items-center">
              <div className="text-xs uppercase tracking-widest text-slate-400 mb-3">{t("mat_preview")}</div>
              <div style={{ width: dim.w * scale, height: dim.h * scale, overflow: "hidden", borderRadius: 8, boxShadow: "0 10px 40px rgba(0,0,0,0.18)" }}>
                <div style={{ transform: `scale(${scale})`, transformOrigin: "top left" }}>
                  <MaterialCanvas ref={canvasRef} type={type} template={template} fields={displayFields} bgUrl={bgUrl} bgColor={bgColor} bgMode={bgMode} dateLabel={DATE_LABELS[activeLang]} locLabel={LOC_LABELS[activeLang]} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 w-full mt-5">
                <Button data-testid="mat-download-png" onClick={exportPng} variant="outline" className="border-th-blue text-th-blue">
                  <Download className="w-4 h-4 mr-2" />{t("mat_download_png")}
                </Button>
                <Button data-testid="mat-download-pdf" onClick={exportPdf} variant="outline" className="border-th-blue text-th-blue">
                  <FileDown className="w-4 h-4 mr-2" />{t("mat_download_pdf")}
                </Button>
              </div>
              <Button data-testid="mat-save" onClick={save} disabled={saving} className="w-full mt-2 bg-th-blue text-white hover:bg-th-bluedark">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {t("mat_save")}
              </Button>
              {editingId && (
                <button data-testid="mat-new" onClick={newMaterial} className="mt-3 text-sm text-slate-500 hover:text-th-blue">+ {t("mat_tab_create")}</button>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* Library */
        <div data-testid="materials-library">
          {library.length === 0 ? (
            <div className="text-center py-20 text-slate-400">
              <FolderOpen className="w-10 h-10 mx-auto mb-4 opacity-50" />
              {t("mat_library_empty")}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
              {library.map((m) => (
                <div key={m.id} data-testid={`mat-card-${m.id}`} className="bg-white rounded-2xl border border-slate-200 overflow-hidden group">
                  <div className="aspect-[3/4] bg-slate-100 overflow-hidden">
                    {m.thumbnail_url ? <img src={m.thumbnail_url} alt={m.title} className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center text-slate-300"><ImageIcon className="w-8 h-8" /></div>}
                  </div>
                  <div className="p-3">
                    <div className="font-semibold text-th-blue text-sm truncate">{m.title}</div>
                    <div className="text-xs text-slate-400 mb-3">{t("mat_type_" + m.type)} · {t("mat_created_by")} {m.author_name || "—"}</div>
                    <div className="flex gap-1.5">
                      <Button size="sm" data-testid={`mat-open-${m.id}`} onClick={() => openMaterial(m)} className="flex-1 h-8 bg-th-blue text-white text-xs">
                        <FolderOpen className="w-3.5 h-3.5 mr-1" />{t("mat_open")}
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`mat-dup-${m.id}`} onClick={() => openMaterial(m, true)} className="h-8 px-2" title={t("mat_duplicate")}>
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`mat-del-${m.id}`} onClick={() => removeMaterial(m.id)} className="h-8 px-2 text-red-600 border-red-200 hover:bg-red-50" title={t("mat_delete")}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
